import { TFunction } from 'react-i18next'

import Product, {
  AuthorizedPerformersOption,
  TWENTY_ONE_PLUS_AUTHORIZED_PERFORMERS,
} from '@models/Product'
import Score from '@models/Score'
import Title from '@models/Title'

export function minimumPurchaseAmount(product?: Product): number {
  return product?.minimum_initial_purchase_amount || 1
}

/** quantity to initialize a title purchase with. */
export function defaultPurchasingQuantity({ product, purchased_product }: Title) {
  return purchased_product && purchased_product?.purchased_quantity > 0
    ? purchased_product.purchased_quantity + 1
    : minimumPurchaseAmount(product)
}

export function numAuthorizedPerformers(product: Product, purchasedQuantity: number) {
  return product.ensemble_size_name === TWENTY_ONE_PLUS_AUTHORIZED_PERFORMERS
    ? '21+'
    : (product.ensemble_size_max ?? 1) * purchasedQuantity
}

export function generateAvailableEnsembleSizes(
  product: Product,
  purchasedQuantity: number
): number[] {
  const ensembleSizes: number[] = []

  const hundredDivisor = Math.ceil(100 / (product.ensemble_size_max ?? 1))
  const initialNewCopies = minimumPurchaseAmount(product)

  for (let i = initialNewCopies + purchasedQuantity; i < hundredDivisor + 1; i++) {
    const newCopies = i - purchasedQuantity
    ensembleSizes.push(newCopies)
  }
  return ensembleSizes
}

/** Compare the ensemble size for a given Product with the quantity of a PurchasedProduct */
export function generateAvailableAuthorizedPerformers(
  product: Product,
  purchasedQuantity: number,
  t: TFunction
) {
  const performerOptions: AuthorizedPerformersOption[] = []

  const resourceText =
    product.ensemble_size_max === 1
      ? 'content.authorizedPerformers.plain.upTo.one'
      : 'content.authorizedPerformers.plain.upTo.other'

  const initialNewCopies = Math.max(minimumPurchaseAmount(product), (purchasedQuantity || 0) + 1)

  if (product.ensemble_size_max) {
    const hundredDivisor = Math.ceil(100 / product.ensemble_size_max)

    for (let i = initialNewCopies; i <= hundredDivisor + 1; i++) {
      const newEnsembleSize = i * product.ensemble_size_max
      performerOptions.push({
        value: i,
        label: t(resourceText, { size: newEnsembleSize }),
      })
    }
  } else {
    performerOptions.push({
      value: initialNewCopies,
      label: t('content.authorizedPerformers.plain.twentyOnePlus'),
    })
  }

  return performerOptions
}

export function hasRatings(product: Product): boolean {
  return product.average_rating > 0
}

export function isProduct(maybeProduct: Score | Product): maybeProduct is Product {
  return maybeProduct.object_type === 'product'
}
